import { Checkbox, FormControlLabel } from "@mui/material";
import type { RefCallback } from "react";
import { useTranslation } from "react-i18next";
import DefaultInput from "@/components/forms/inputs/defaultInput";
import styles from "@/components/lms/lmsCustomIntervalFields.module.css";

export interface LmsCustomIntervalState {
  useCustomIntervals: boolean;
  completionIntervalInDays: string;
  validityIntervalInDays: string;
  renewalCooldownInDays?: string;
}

export const getDefaultCustomIntervalState = (): LmsCustomIntervalState => ({
  useCustomIntervals: false,
  completionIntervalInDays: "",
  validityIntervalInDays: "",
});

export const getDefaultAssignmentIntervalState =
  (): LmsCustomIntervalState => ({
    ...getDefaultCustomIntervalState(),
    renewalCooldownInDays: "",
  });

const isFilled = (value: string) => value.trim() !== "";

export const isValidCustomIntervalValue = (value: string) => {
  if (!isFilled(value)) return true;
  const numericValue = Number(value);
  return Number.isSafeInteger(numericValue) && numericValue >= 0;
};

export const canSubmitCustomIntervals = (value: LmsCustomIntervalState) =>
  getInvalidCustomIntervalTargets(value).length === 0;

export const CUSTOM_INTERVAL_VALIDATION_TARGETS = [
  "completionInterval",
  "renewalCooldown",
  "validityInterval",
] as const;

export type LmsCustomIntervalValidationTarget =
  (typeof CUSTOM_INTERVAL_VALIDATION_TARGETS)[number];

export const isCustomIntervalValidationTarget = (
  target: string
): target is LmsCustomIntervalValidationTarget =>
  (CUSTOM_INTERVAL_VALIDATION_TARGETS as readonly string[]).includes(target);

export const getInvalidCustomIntervalTargets = ({
  useCustomIntervals,
  completionIntervalInDays,
  validityIntervalInDays,
  renewalCooldownInDays = "",
}: LmsCustomIntervalState): LmsCustomIntervalValidationTarget[] => {
  if (!useCustomIntervals) return [];

  if (
    !isFilled(completionIntervalInDays) &&
    !isFilled(validityIntervalInDays) &&
    !isFilled(renewalCooldownInDays)
  ) {
    return ["completionInterval"];
  }

  const invalidTargets: LmsCustomIntervalValidationTarget[] = [];
  if (!isValidCustomIntervalValue(completionIntervalInDays)) {
    invalidTargets.push("completionInterval");
  }
  if (!isValidCustomIntervalValue(renewalCooldownInDays)) {
    invalidTargets.push("renewalCooldown");
  }
  if (!isValidCustomIntervalValue(validityIntervalInDays)) {
    invalidTargets.push("validityInterval");
  }
  return invalidTargets;
};

export const buildCustomIntervalPayload = (
  {
    useCustomIntervals,
    completionIntervalInDays,
    validityIntervalInDays,
    renewalCooldownInDays,
  }: LmsCustomIntervalState,
  options: { includeNullForEmpty: boolean } = { includeNullForEmpty: false }
) => {
  const payload: Record<string, number | null> = {};
  const intervals = {
    completion_interval_in_days: completionIntervalInDays,
    validity_interval_in_days: validityIntervalInDays,
    renewal_cooldown_in_days: renewalCooldownInDays,
  };

  for (const [field, value] of Object.entries(intervals)) {
    if (value === undefined) continue;
    if (useCustomIntervals && isFilled(value)) {
      payload[field] = Number(value);
    } else if (options.includeNullForEmpty) {
      payload[field] = null;
    }
  }

  return payload;
};

interface Props {
  value: LmsCustomIntervalState;
  onChange: (value: LmsCustomIntervalState) => void;
  invalidTargets?: readonly LmsCustomIntervalValidationTarget[];
  registerCompletionTarget?: RefCallback<HTMLElement>;
  registerValidityTarget?: RefCallback<HTMLElement>;
  registerRenewalCooldownTarget?: RefCallback<HTMLElement>;
}

interface DayInputConfig {
  name: string;
  labelKey: string;
  tooltipKey?: string;
  fieldValue: string;
  validationTarget: LmsCustomIntervalValidationTarget;
  registerTarget?: RefCallback<HTMLElement>;
  onValueChange: (nextValue: string) => void;
}

export default function LmsCustomIntervalFields({
  value,
  onChange,
  invalidTargets = [],
  registerCompletionTarget,
  registerValidityTarget,
  registerRenewalCooldownTarget,
}: Props) {
  const { t } = useTranslation();

  const updateValue = (nextValue: Partial<LmsCustomIntervalState>) => {
    onChange({
      ...value,
      ...nextValue,
    });
  };

  const renderDayInput = ({
    name,
    labelKey,
    tooltipKey,
    fieldValue,
    validationTarget,
    registerTarget,
    onValueChange,
  }: DayInputConfig) => {
    const hasInvalidValue = !isValidCustomIntervalValue(fieldValue);
    const hasError =
      hasInvalidValue || invalidTargets.includes(validationTarget);
    const errorId = `${name}-error`;

    return (
      <div>
        <DefaultInput
          ref={registerTarget}
          label={t(labelKey)}
          infoTooltip={Boolean(tooltipKey)}
          infoTooltipMessage={tooltipKey ? t(tooltipKey) : undefined}
          name={name}
          type="number"
          min={0}
          step={1}
          value={fieldValue}
          onChange={(event) => onValueChange(event.target.value)}
          width="100%"
          inputStyle={{ borderColor: hasError ? "red" : undefined }}
          aria-invalid={hasError}
          aria-describedby={hasError ? errorId : undefined}
        />
        {hasError && (
          <span id={errorId} className={styles.errorMessage} role="alert">
            {hasInvalidValue
              ? t("field_must_be_zero_or_greater")
              : t("field_required")}
          </span>
        )}
      </div>
    );
  };

  return (
    <>
      <FormControlLabel
        label={t("set_custom_intervals")}
        control={
          <Checkbox
            checked={value.useCustomIntervals}
            onChange={(event) =>
              updateValue({ useCustomIntervals: event.target.checked })
            }
            color="secondary"
          />
        }
      />
      {value.useCustomIntervals && (
        <div className={styles.intervalFieldsWrapper}>
          {renderDayInput({
            name: "lms-completion-interval",
            labelKey: "completion_interval_in_days",
            fieldValue: value.completionIntervalInDays,
            validationTarget: "completionInterval",
            registerTarget: registerCompletionTarget,
            onValueChange: (nextValue) =>
              updateValue({ completionIntervalInDays: nextValue }),
          })}
          {value.renewalCooldownInDays !== undefined &&
            renderDayInput({
              name: "lms-renewal-cooldown",
              labelKey: "renewal_cooldown_in_days",
              tooltipKey: "assignment_renewal_cooldown_in_days_help",
              fieldValue: value.renewalCooldownInDays,
              validationTarget: "renewalCooldown",
              registerTarget: registerRenewalCooldownTarget,
              onValueChange: (nextValue) =>
                updateValue({ renewalCooldownInDays: nextValue }),
            })}
          {renderDayInput({
            name: "lms-validity-interval",
            labelKey: "validity_interval_in_days",
            tooltipKey: "validity_interval_help",
            fieldValue: value.validityIntervalInDays,
            validationTarget: "validityInterval",
            registerTarget: registerValidityTarget,
            onValueChange: (nextValue) =>
              updateValue({ validityIntervalInDays: nextValue }),
          })}
        </div>
      )}
    </>
  );
}
